import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { classifyCreateError, useCreateStaff } from './useStaff';

/**
 * + New staff — slice 17w. Follows `NewTenantDialog` exactly: a modal with the
 * same field chrome, the same submit-disabled-until-ready gate, and the same
 * "nothing sends this password, tell them yourself" confirmation on success.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THREE THINGS THIS FORM DELIBERATELY DOES NOT DO.
 * ─────────────────────────────────────────────────────────────────────────────
 *   - It has NO role picker. The API cannot create a super_admin (`createStaff`
 *     hardcodes `admin_staff`), so a picker would imply a choice that does not
 *     exist. The created role is stated as informational text instead.
 *   - It never keeps the password. It lives in local state only until submit
 *     resolves, then `reset()` clears it. It is never logged and never shown.
 *   - It does not echo `hasPassword` as anything password-shaped — that field
 *     is on the list row, as a plain enabled/disabled-adjacent fact, not here.
 */

const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary';

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  // The hint sits OUTSIDE the <label> so it does not become part of the
  // control's accessible name — the same fix `NewTenantDialog` records.
  return (
    <div className="block">
      <label className="block">
        <span className="mb-1 block text-[12px] font-medium text-raad-muted">{label}</span>
        {children}
      </label>
      {hint ? <span className="mt-1 block text-[11.5px] text-raad-muted">{hint}</span> : null}
    </div>
  );
}

export function NewStaffDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ name: string; phone: string } | null>(null);
  const create = useCreateStaff();

  // Mirrors the server floor: name present, phone plausible, password >= 12.
  const ready = name.trim().length > 0 && phone.trim().length >= 7 && password.length >= 12;

  async function submit() {
    if (!ready) return;
    setError(null);
    const typedPhone = phone.trim();
    const typedName = name.trim();
    try {
      await create.mutateAsync({ name: typedName, phone: typedPhone, password });
      setDone({ name: typedName, phone: typedPhone });
      // 🔴 Clear the plaintext the instant it is no longer needed. Nothing below
      // reads it; keeping it in state would be a live secret with no purpose.
      setPassword('');
    } catch (e) {
      const kind = classifyCreateError(e);
      setError(t(`staff.createError.${kind}`));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('staff.newStaff.title')}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-lg sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">{t('staff.newStaff.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('staff.newStaff.close')}
          </button>
        </div>

        {done ? (
          <div>
            <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12.5px]">
              {t('staff.newStaff.created', { name: done.name })}
            </p>
            {/* Said explicitly — nothing sends this password. The super-admin
                who typed it is the only channel it has. */}
            <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('staff.newStaff.tellThemPassword', { phone: done.phone })}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white"
              >
                {t('staff.newStaff.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* The role is informational, never a choice — see the header. */}
            <p className="mb-4 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12px] text-raad-muted">
              {t('staff.newStaff.roleInfo')}
            </p>

            <div className="grid gap-4">
              <Field label={t('staff.newStaff.name')}>
                <input
                  className={INPUT}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </Field>

              <Field label={t('staff.newStaff.phone')} hint={t('staff.newStaff.phoneHint')}>
                <input
                  className={INPUT}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  placeholder="+2526…"
                />
              </Field>

              <Field label={t('staff.newStaff.password')} hint={t('staff.newStaff.passwordHint')}>
                <input
                  className={INPUT}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
            </div>

            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={submit}
                disabled={!ready || create.isPending}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {create.isPending ? t('staff.newStaff.creating') : t('staff.newStaff.create')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
